import { useCallback, useMemo, useRef, useState } from "react";
import {
  parse,
  COUNTS_TO_MV,
  peakRetentionMin,
  type HwFile,
} from "./hw/parser";
import { tr, trf } from "./i18n";
import { Watermark } from "./components/Watermark";
import { Chromatogram } from "./components/Chromatogram";
import {
  AuditTab,
  ComponentsTab,
  InfoTab,
  LayoutTab,
  PeaksTab,
  ReportTab,
} from "./components/Tabs";

const TAB_KEYS = [
  "Overview",
  "Peaks",
  "Components",
  "Audit",
  "Report",
  "Layout",
] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** ImPlot can handle a lot, but on huge traces (e.g. PONA = 277500 samples)
 *  interaction stays smoother if we cap point count. Ported from
 *  cmd/hwgui/main.go. */
const MAX_PLOT_POINTS = 20000;
const SIMILARITY_BINS = 512;

interface Loaded {
  id: string;
  path: string;
  file: HwFile;
  xs: Float64Array;
  ys: Float64Array;
  pattern: Float64Array | null;
  durationMin: number;
}

interface SimilarityResult {
  index: number;
  score: number | null;
}

interface SimilaritySearch {
  queryId: string;
  queryPath: string;
  results: SimilarityResult[];
}

export function App() {
  const [loadedFiles, setLoadedFiles] = useState<Loaded[]>([]);
  const [activeLoadedIndex, setActiveLoadedIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("Overview");
  const [dropActive, setDropActive] = useState(false);
  const [hoveredPeakIndex, setHoveredPeakIndex] = useState<number | null>(null);
  const [similaritySearch, setSimilaritySearch] = useState<SimilaritySearch | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loaded = loadedFiles[activeLoadedIndex] ?? null;

  const loadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setError(null);
    try {
      const batchId = Date.now();
      const nextLoaded = await Promise.all(
        files.map(async (file, index) => {
          const buf = await file.arrayBuffer();
          const parsed = parse(buf);
          const { xs, ys } = decimate(parsed);
          return {
            id: `${batchId}:${index}:${file.name}:${file.size}:${file.lastModified}`,
            path: file.name,
            file: parsed,
            xs,
            ys,
            pattern: signalPattern(parsed.Signal),
            durationMin: signalDurationMin(parsed),
          };
        }),
      );
      setHoveredPeakIndex(null);
      setSimilaritySearch(null);
      setLoadedFiles(nextLoaded);
      setActiveLoadedIndex(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSimilaritySearch(null);
      setLoadedFiles([]);
      setActiveLoadedIndex(0);
    }
  }, []);

  const onPick = () => fileInputRef.current?.click();

  const onPickedFiles = (files: FileList | null) => {
    if (files && files.length > 0) {
      void loadFiles(Array.from(files));
    }
  };

  // Drag-and-drop handlers attached to the whole window. We track depth
  // so we don't lose the overlay when the cursor moves between children.
  const dragDepth = useRef(0);
  const onDragEnter = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current++;
    setDropActive(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDropActive(false);
    if (e.dataTransfer.files.length > 0) {
      void loadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const pathLabel = useMemo(() => {
    if (error) return { text: trf("ERROR: %s", error), error: true };
    if (!loaded)
      return {
        text: tr("(no file loaded — click Open or drag .hw files here)"),
        error: false,
      };
    const f = loaded.file;
    if (f.ParseError) {
      return {
        text: trf("%s   [PARSE ERROR: %s]", loaded.path, f.ParseError),
        error: true,
      };
    }
    return {
      text: trf(
        "%s   [%s, %d samples @ %s Hz]",
        loaded.path,
        f.Magic,
        f.SampleCount,
        f.SamplingHz.toFixed(2),
      ),
      error: false,
    };
  }, [loaded, error]);

  const peakPositions = useMemo(() => {
    if (!loaded) return [];
    return loaded.file.Peaks.map((p) =>
      peakRetentionMin(p, loaded.file.SamplingHz),
    );
  }, [loaded]);

  const namedPeaks = useMemo(() => {
    if (!loaded) return [];
    return loaded.file.Peaks.flatMap((p) => {
      const name = p.Name.trim();
      if (!name) return [];
      return [{
        index: p.Index,
        x: peakRetentionMin(p, loaded.file.SamplingHz),
        name,
      }];
    });
  }, [loaded]);

  const highlightedPeakX =
    hoveredPeakIndex == null ? null : peakPositions[hoveredPeakIndex] ?? null;

  const onChartHoverPoint = useCallback(
    (point: { x: number } | null) => {
      if (!point || peakPositions.length === 0) {
        setHoveredPeakIndex((current) => (current === null ? current : null));
        return;
      }
      const next = nearestPeakIndex(peakPositions, point.x);
      setHoveredPeakIndex((current) => (current === next ? current : next));
    },
    [peakPositions],
  );

  const onPeakHover = useCallback((index: number | null) => {
    setHoveredPeakIndex((current) => (current === index ? current : index));
  }, []);

  const selectLoadedFile = useCallback((index: number) => {
    setActiveLoadedIndex(index);
    setHoveredPeakIndex(null);
  }, []);

  const searchSimilarPatterns = useCallback(() => {
    const query = loadedFiles[activeLoadedIndex];
    if (!query) return;
    setSimilaritySearch({
      queryId: query.id,
      queryPath: query.path,
      results: rankSimilarPatterns(loadedFiles, activeLoadedIndex),
    });
  }, [activeLoadedIndex, loadedFiles]);

  return (
    <div
      className={"app" + (dropActive ? " drop-active" : "")}
      data-drop-text={tr("Drop .hw files to load them")}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Watermark />

      <div className="toolbar">
        <button onClick={onPick}>{tr("Open...")}</button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".hw,application/octet-stream"
          style={{ display: "none" }}
          onChange={(e) => {
            onPickedFiles(e.target.files);
            // Reset so picking the same file again still triggers onChange.
            e.target.value = "";
          }}
        />
        <span className={"path-label" + (pathLabel.error ? " error" : "")}>
          {pathLabel.text}
        </span>
        <div className="toolbar-right">
          <span className="target-info">
            {tr(
              "Designed for Ruineng GC3900 II, workstation software 3900-0914A (Qianpu remote-control build)",
            )}
          </span>
          <span className="beta-warning" role="status">
            {tr(
              "Beta — verify results against the original workstation software.",
            )}
          </span>
        </div>
      </div>

      <Splitter
        left={
          <div className="split-pane plot-pane">
            {loaded && loaded.xs.length > 0 ? (
              <Chromatogram
                xs={loaded.xs}
                ys={loaded.ys}
                title={tr("Chromatogram")}
                xLabel={tr("Retention time (min)")}
                yLabel={tr("Signal (mV)")}
                namedPeaks={namedPeaks}
                highlightedX={highlightedPeakX}
                onHoverPoint={onChartHoverPoint}
              />
            ) : (
              <div className="empty-state">{tr("No signal to display.")}</div>
            )}
          </div>
        }
        right={
          <div className="split-pane side-pane">
            {loadedFiles.length > 0 && (
              <div className="side-row">
                <LoadedFilesList
                  files={loadedFiles}
                  activeIndex={activeLoadedIndex}
                  onSelect={selectLoadedFile}
                />
                {loadedFiles.length > 1 && (
                  <SimilaritySearchPanel
                    files={loadedFiles}
                    activeIndex={activeLoadedIndex}
                    search={similaritySearch}
                    onSearch={searchSimilarPatterns}
                    onSelect={selectLoadedFile}
                  />
                )}
              </div>
            )}
            {!loaded ? (
              <div className="empty-state">
                {error
                  ? trf("Load error:\n%s", error)
                  : tr("No file loaded.")}
              </div>
            ) : (
              <>
                <div className="tab-bar">
                  {TAB_KEYS.map((k) => (
                    <button
                      key={k}
                      className={tab === k ? "active" : ""}
                      onClick={() => setTab(k)}
                    >
                      {tr(k)}
                    </button>
                  ))}
                </div>
                <div className="tab-body">
                  {tab === "Overview" && <InfoTab file={loaded.file} />}
                  {tab === "Peaks" && (
                    <PeaksTab
                      file={loaded.file}
                      highlightedPeakIndex={hoveredPeakIndex}
                      onHoverPeak={onPeakHover}
                    />
                  )}
                  {tab === "Components" && (
                    <ComponentsTab file={loaded.file} />
                  )}
                  {tab === "Audit" && <AuditTab file={loaded.file} />}
                  {tab === "Report" && <ReportTab file={loaded.file} />}
                  {tab === "Layout" && <LayoutTab file={loaded.file} />}
                </div>
              </>
            )}
          </div>
        }
      />
    </div>
  );
}

interface LoadedFilesListProps {
  files: Loaded[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

function LoadedFilesList({ files, activeIndex, onSelect }: LoadedFilesListProps) {
  return (
    <div className="loaded-files-panel" aria-label={tr("Loaded files")}>
      <div className="loaded-files-header">
        {trf("Loaded files (%d)", files.length)}
      </div>
      <div className="loaded-files-list">
        {files.map((item, index) => {
          const active = index === activeIndex;
          const parseError = item.file.ParseError;
          return (
            <button
              key={item.id}
              type="button"
              className={
                "loaded-file-button" +
                (active ? " active" : "") +
                (parseError ? " has-error" : "")
              }
              title={
                parseError
                  ? trf("%s — Parse error: %s", item.path, parseError)
                  : trf(
                      "%s — %d samples, %d peaks",
                      item.path,
                      item.file.SampleCount,
                      item.file.Peaks.length,
                    )
              }
              aria-current={active ? "true" : undefined}
              aria-label={trf("Select %s", item.path)}
              onClick={() => onSelect(index)}
            >
              <span className="loaded-file-index">{index + 1}</span>
              <span className="loaded-file-name">{item.path}</span>
              <span className="loaded-file-meta">
                {parseError
                  ? tr("parse error")
                  : trf("%d peaks", item.file.Peaks.length)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface SimilaritySearchPanelProps {
  files: Loaded[];
  activeIndex: number;
  search: SimilaritySearch | null;
  onSearch: () => void;
  onSelect: (index: number) => void;
}

function SimilaritySearchPanel({
  files,
  activeIndex,
  search,
  onSearch,
  onSelect,
}: SimilaritySearchPanelProps) {
  const active = files[activeIndex];
  return (
    <div className="similarity-panel" aria-label={tr("Pattern search")}>
      <div className="similarity-header">
        <div
          className="similarity-title"
          title={tr("Compares normalized full-signal shape across all loaded files.")}
        >
          {tr("Pattern search")}
        </div>
        <button type="button" onClick={onSearch} disabled={!active?.pattern}>
          {tr("Search")}
        </button>
      </div>
      {search && (
        <>
          <div className="similarity-query" title={search.queryPath}>
            {trf("Query: %s", search.queryPath)}
          </div>
          <div className="similarity-results">
            {search.results.map((result) => {
              const item = files[result.index];
              if (!item) return null;
              const isQuery = item.id === search.queryId;
              const scoreText = formatSimilarityScore(result.score);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={
                    "similarity-result" +
                    (result.index === activeIndex ? " active" : "") +
                    (isQuery ? " query" : "")
                  }
                  title={trf("Similarity to %s: %s", search.queryPath, scoreText)}
                  onClick={() => onSelect(result.index)}
                >
                  <span className="similarity-score">{scoreText}</span>
                  <span className="similarity-file-name">{item.path}</span>
                  <span className="similarity-meta">
                    {isQuery
                      ? tr("query")
                      : trf(
                          "%d peaks, %s min",
                          item.file.Peaks.length,
                          formatDurationMin(item.durationMin),
                        )}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasFiles(e: React.DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  for (let i = 0; i < types.length; i++) {
    if (types[i] === "Files") return true;
  }
  return false;
}

/** Decimates the signal trace to at most MAX_PLOT_POINTS samples and
 *  converts raw detector counts to mV (matching HWgram's Y axis).
 *  Mirrors the loadFile() routine in cmd/hwgui/main.go. */
function decimate(f: HwFile): { xs: Float64Array; ys: Float64Array } {
  const n = f.Signal.length;
  if (n === 0) return { xs: new Float64Array(0), ys: new Float64Array(0) };
  let stride = 1;
  if (n > MAX_PLOT_POINTS) {
    stride = Math.ceil(n / MAX_PLOT_POINTS);
  }
  const dt = f.SamplingHz > 0 ? 1 / f.SamplingHz / 60 : 1 / 60;
  const out = Math.ceil(n / stride);
  const xs = new Float64Array(out);
  const ys = new Float64Array(out);
  let j = 0;
  for (let i = 0; i < n; i += stride) {
    xs[j] = i * dt;
    ys[j] = f.Signal[i] * COUNTS_TO_MV;
    j++;
  }
  return { xs: xs.subarray(0, j), ys: ys.subarray(0, j) };
}

function nearestPeakIndex(positions: number[], x: number): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < positions.length; i++) {
    const d = Math.abs(positions[i] - x);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

function signalDurationMin(f: HwFile): number {
  if (f.SamplingHz <= 0) return 0;
  return f.Signal.length / f.SamplingHz / 60;
}

function signalPattern(signal: Int32Array): Float64Array | null {
  if (signal.length < 2) return null;

  const out = new Float64Array(SIMILARITY_BINS);
  for (let i = 0; i < SIMILARITY_BINS; i++) {
    const pos = (i / (SIMILARITY_BINS - 1)) * (signal.length - 1);
    const left = Math.floor(pos);
    const right = Math.min(signal.length - 1, left + 1);
    const frac = pos - left;
    out[i] = signal[left] * (1 - frac) + signal[right] * frac;
  }

  let mean = 0;
  for (let i = 0; i < out.length; i++) mean += out[i];
  mean /= out.length;

  let sumSq = 0;
  for (let i = 0; i < out.length; i++) {
    out[i] -= mean;
    sumSq += out[i] * out[i];
  }

  const rms = Math.sqrt(sumSq / out.length);
  if (!Number.isFinite(rms) || rms <= 0) return null;
  for (let i = 0; i < out.length; i++) out[i] /= rms;
  return out;
}

function rankSimilarPatterns(files: Loaded[], queryIndex: number): SimilarityResult[] {
  const query = files[queryIndex]?.pattern;
  return files
    .map((item, index) => ({
      index,
      score: query && item.pattern ? patternSimilarityScore(query, item.pattern) : null,
    }))
    .sort((a, b) => {
      if (a.score == null && b.score == null) return a.index - b.index;
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      return b.score - a.score || a.index - b.index;
    });
}

function patternSimilarityScore(a: Float64Array, b: Float64Array): number {
  let dot = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) dot += a[i] * b[i];
  const correlation = dot / n;
  return Math.max(0, Math.min(1, correlation)) * 100;
}

function formatSimilarityScore(score: number | null): string {
  return score == null ? tr("n/a") : `${score.toFixed(1)}%`;
}

function formatDurationMin(durationMin: number): string {
  if (!Number.isFinite(durationMin) || durationMin <= 0) return tr("n/a");
  return durationMin >= 10 ? durationMin.toFixed(1) : durationMin.toFixed(2);
}

// ---------------------------------------------------------------------------
// Resizable horizontal splitter
// ---------------------------------------------------------------------------

interface SplitterProps {
  left: React.ReactNode;
  right: React.ReactNode;
}

function Splitter({ left, right }: SplitterProps) {
  // Same default as cmd/hwgui/main.go (`var splitPos float32 = 760`).
  const [leftPx, setLeftPx] = useState<number>(760);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const onMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const min = 320;
      const max = rect.width - 360;
      const next = Math.min(max, Math.max(min, ev.clientX - rect.left));
      setLeftPx(next);
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={containerRef}
      className="split"
      style={{ gridTemplateColumns: `${leftPx}px 6px minmax(360px, 1fr)` }}
    >
      {left}
      <div className="gutter" onMouseDown={onMouseDown} />
      {right}
    </div>
  );
}
